import React, { useState, useEffect } from 'react';
import { API } from '../utils/api';
import axios from 'axios';
import ConfirmModal from './modals/ConfirmModal';
import SuccessModal from './modals/SuccessModal';
import ErrorModal from './modals/ErrorModal';

// Collapsible Section Component - Dark Theme (defined outside to maintain stable reference)
const CollapsibleSection = ({ sectionId, title, tagline, icon, children, bgColor = "bg-purple-600", hoverColor = "hover:bg-purple-500", isNew = false, isOpen, onToggle }) => {
  return (
    <div className="border-b border-slate-700 last:border-b-0">
      <div
        onClick={() => onToggle(sectionId)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle(sectionId);
          }
        }}
        role="button"
        tabIndex={0}
        aria-expanded={isOpen}
        className={`w-full ${bgColor} ${hoverColor} text-white transition-colors duration-150 cursor-pointer`}
      >
        <div className="px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {icon && (
              <svg className="w-6 h-6 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                {icon}
              </svg>
            )}
            <div className="text-left">
              <div className="flex items-center space-x-2">
                <h2 className="text-lg font-semibold">{title}</h2>
                {isNew && (
                  <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs font-semibold rounded-full border border-green-500/30">
                    NEW
                  </span>
                )}
              </div>
              {tagline && (
                <p className="text-purple-200 text-sm mt-0.5">{tagline}</p>
              )}
            </div>
          </div>
          <svg
            className={`w-5 h-5 text-white flex-shrink-0 transition-transform duration-200 ${
              isOpen ? 'rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {isOpen && (
        <div className="px-6 py-6 bg-slate-800">
          {children}
        </div>
      )}
    </div>
  );
};

const TeamDashboardSettings = () => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [minPhotosRequired, setMinPhotosRequired] = useState(5);
  const [autoClockOut, setAutoClockOut] = useState(false);
  const [autoClockOutTime, setAutoClockOutTime] = useState('18:00');
  const [selfieRequired, setSelfieRequired] = useState(false);
  const [bankHolidaysEnabled, setBankHolidaysEnabled] = useState(false);
  const [bankHolidays, setBankHolidays] = useState([]);
  const [loadingBankHolidays, setLoadingBankHolidays] = useState(false);
  const [companyClosuresEnabled, setCompanyClosuresEnabled] = useState(false);
  const [companyClosures, setCompanyClosures] = useState([]);
  const [newClosurePeriod, setNewClosurePeriod] = useState({
    name: '',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [editingClosureId, setEditingClosureId] = useState(null);
  const [editClosureData, setEditClosureData] = useState({
    name: '',
    start_date: '',
    end_date: '',
    reason: ''
  });
  const [hasChanges, setHasChanges] = useState(false);
  
  // Organisation Settings state
  const [organisationData, setOrganisationData] = useState({
    name: '',
    street_address: '',
    city: '',
    postcode: '',
    country: '',
    website: '',
    email: '',
    phone: '',
    company_number: '',
    logo: '',
    organisation_id: ''  // Add organisation_id
  });
  const [organisationMessage, setOrganisationMessage] = useState('');
  
  // Logo upload state
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoError, setLogoError] = useState('');

  // Custom modal states
  const [confirmModal, setConfirmModal] = useState({ show: false, message: '', onConfirm: null });
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  const [organisationLoading, setOrganisationLoading] = useState(false);
  
  // Site Opening Hours state
  const [siteOpeningHours, setSiteOpeningHours] = useState({
    use_same_hours_for_all: true,
    day_specific_hours: {
      monday: { enabled: true, start_time: '08:00', end_time: '17:00' },
      tuesday: { enabled: true, start_time: '08:00', end_time: '17:00' },
      wednesday: { enabled: true, start_time: '08:00', end_time: '17:00' },
      thursday: { enabled: true, start_time: '08:00', end_time: '17:00' },
      friday: { enabled: true, start_time: '08:00', end_time: '17:00' },
      saturday: { enabled: false, start_time: '08:00', end_time: '17:00' },
      sunday: { enabled: false, start_time: '08:00', end_time: '17:00' }
    },
    // DEPRECATED fields (keep for backward compatibility)
    working_days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    start_time: '08:00',
    end_time: '17:00',
    daily_break_duration: 1 // in hours
  });

  // Offline Clock In/Out state
  const [sites, setSites] = useState([]);
  const [siteSettings, setSiteSettings] = useState({}); // Map of site_id -> settings
  const [offlineGlobalSettings, setOfflineGlobalSettings] = useState({
    selfie_required: true,
    max_offline_duration: 24, // hours
    gps_radius: 500 // meters (200-500)
  });
  const [loadingSites, setLoadingSites] = useState(false);

  // Subscription features state
  const [subscriptionFeatures, setSubscriptionFeatures] = useState(null);

  // Collapsible section states - lifted to parent to survive re-renders
  const [openSections, setOpenSections] = useState({
    organisationDetails: true,
    offlineClockIn: true,
    siteOpeningHours: true,
    ukBankHolidays: true,
    companyClosures: true,
    photoRequirements: true,
    autoClockOut: true,
    selfieVerification: true
  });

  useEffect(() => {
    fetchAllSettings();
    fetchSitesAndSettings();
  }, []);

  // Fetch ALL settings (unified: company details + configuration settings)
  const fetchAllSettings = async () => {
    try {
      setLoading(true);
      // SINGLE unified endpoint for ALL data
      const response = await axios.get(`${API}/admin/organisation`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data) {
        console.log('=== ADMIN SETTINGS: Fetched Data ===', response.data);
        
        // Set company details
        setOrganisationData({
          name: response.data.name || '',
          street_address: response.data.street_address || '',
          city: response.data.city || '',
          postcode: response.data.postcode || '',
          country: response.data.country || '',
          website: response.data.website || '',
          email: response.data.email || '',
          phone: response.data.phone || '',
          company_number: response.data.company_number || '',
          logo: response.data.logo || '',
          organisation_id: response.data.organisation_id || ''
        });
        
        console.log('=== ADMIN SETTINGS: Set Organisation Data ===', {
          name: response.data.name || '',
          street_address: response.data.street_address || '',
          city: response.data.city || '',
          postcode: response.data.postcode || '',
          country: response.data.country || '',
          email: response.data.email || '',
          phone: response.data.phone || ''
        });
        
        // Set configuration settings
        setSettings(response.data);
        setMinPhotosRequired(response.data.min_photos_required);
        setAutoClockOut(response.data.auto_clock_out || false);
        setAutoClockOutTime(response.data.auto_clock_out_time || '18:00');
        setSelfieRequired(response.data.selfie_required || false);
        setBankHolidaysEnabled(response.data.bank_holidays_enabled || false);
        setCompanyClosuresEnabled(response.data.company_closures_enabled || false);
        
        // Set subscription features
        setSubscriptionFeatures(response.data.subscription_features || {});
        
        setSiteOpeningHours({
          use_same_hours_for_all: response.data.use_same_hours_for_all ?? true,
          day_specific_hours: response.data.day_specific_hours || {
            monday: { enabled: true, start_time: '08:00', end_time: '17:00' },
            tuesday: { enabled: true, start_time: '08:00', end_time: '17:00' },
            wednesday: { enabled: true, start_time: '08:00', end_time: '17:00' },
            thursday: { enabled: true, start_time: '08:00', end_time: '17:00' },
            friday: { enabled: true, start_time: '08:00', end_time: '17:00' },
            saturday: { enabled: false, start_time: '08:00', end_time: '17:00' },
            sunday: { enabled: false, start_time: '08:00', end_time: '17:00' }
          },
          // DEPRECATED fields
          working_days: response.data.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
          start_time: response.data.start_time || '08:00',
          end_time: response.data.end_time || '17:00',
          daily_break_duration: response.data.daily_break_duration || 1
        });
        
        // If bank holidays are enabled, fetch the holidays
        if (response.data.bank_holidays_enabled) {
          await fetchBankHolidays();
        }
        
        // Always fetch company closures (they exist independently of the enabled flag)
        await fetchCompanyClosures();
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch company closures from the API
  const fetchCompanyClosures = async () => {
    try {
      const response = await axios.get(`${API}/admin/company-closures`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      if (response.data) {
        setCompanyClosures(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch company closures:', error);
      // Don't fail silently - closures might just not exist yet
      setCompanyClosures([]);
    }
  };

  // Handle organisation input changes
  const handleOrganisationInputChange = (e) => {
    const { name, value } = e.target;
    setOrganisationData(prev => ({
      ...prev,
      [name]: value
    }));
    setHasChanges(true);
  };

  // Function to fetch UK bank holidays
  const fetchBankHolidays = async () => {
    try {
      setLoadingBankHolidays(true);
      
      // Use UK Government API for bank holidays
      const currentYear = new Date().getFullYear();
      const response = await fetch(`https://www.gov.uk/bank-holidays.json`);
      const data = await response.json();
      
      // Get England and Wales bank holidays for current year
      const englandWales = data['england-and-wales'].events.filter(event => 
        new Date(event.date).getFullYear() === currentYear
      );
      
      setBankHolidays(englandWales);
    } catch (error) {
      console.error('Failed to fetch bank holidays:', error);
      // Fallback to static bank holidays for current year
      const staticHolidays = getStaticBankHolidays();
      setBankHolidays(staticHolidays);
    } finally {
      setLoadingBankHolidays(false);
    }
  };

  // Fallback static bank holidays (approximate dates for current year)
  const getStaticBankHolidays = () => {
    const currentYear = new Date().getFullYear();
    return [
      { title: "New Year's Day", date: `${currentYear}-01-01` },
      { title: "Good Friday", date: `${currentYear}-03-29` }, // Approximate
      { title: "Easter Monday", date: `${currentYear}-04-01` }, // Approximate
      { title: "Early May Bank Holiday", date: `${currentYear}-05-06` }, // Approximate
      { title: "Spring Bank Holiday", date: `${currentYear}-05-27` }, // Approximate
      { title: "Summer Bank Holiday", date: `${currentYear}-08-26` }, // Approximate
      { title: "Christmas Day", date: `${currentYear}-12-25` },
      { title: "Boxing Day", date: `${currentYear}-12-26` }
    ];
  };

  // Handle logo file selection
  const handleLogoFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setLogoError('Please select an image file');
        return;
      }
      // Validate file size (2MB max)
      if (file.size > 2 * 1024 * 1024) {
        setLogoError('File size must be less than 2MB');
        return;
      }
      setLogoFile(file);
      setLogoPreview(URL.createObjectURL(file));
      setLogoError('');
    }
  };

  // Upload logo to server
  const handleLogoUpload = async () => {
    if (!logoFile) return;
    
    setUploadingLogo(true);
    setLogoError('');
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', logoFile);
      
      const response = await axios.post(
        `${API}/admin/organisation/logo`,
        formDataUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );
      
      // Update organisation data with new logo URL
      setOrganisationData(prev => ({ ...prev, logo: response.data.logo_url }));
      setLogoFile(null);
      setLogoPreview(null);
      setSuccessModal({ show: true, message: 'Organisation logo updated successfully!' });
    } catch (err) {
      console.error('Logo upload failed:', err);
      setLogoError(err.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Delete logo
  const handleLogoDelete = async () => {
    setUploadingLogo(true);
    try {
      await axios.delete(`${API}/admin/organisation/logo`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setOrganisationData(prev => ({ ...prev, logo: '' }));
      setLogoFile(null);
      setLogoPreview(null);
      setSuccessModal({ show: true, message: 'Organisation logo removed successfully!' });
    } catch (err) {
      console.error('Logo delete failed:', err);
      setLogoError(err.response?.data?.detail || 'Failed to delete logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Fetch all sites and their offline settings
  const fetchSitesAndSettings = async () => {
    try {
      setLoadingSites(true);
      
      // Fetch all sites for the organisation
      const sitesResponse = await axios.get(`${API}/admin/sites`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setSites(sitesResponse.data);
      
      // Fetch settings for each site
      const settingsMap = {};
      for (const site of sitesResponse.data) {
        try {
          const settingsResponse = await axios.get(
            `${API}/admin/sites/${site.id}/offline-settings`,
            { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
          );
          settingsMap[site.id] = settingsResponse.data;
        } catch (error) {
          // If settings don't exist, use defaults
          settingsMap[site.id] = {
            site_id: site.id,
            allow_offline_clockin: false,
            gps_validation_radius: 500.0,
            require_selfie: true,
            max_offline_duration: 24,
            site_latitude: null,
            site_longitude: null
          };
        }
      }
      
      setSiteSettings(settingsMap);
      
      // Set global defaults from first site or use defaults
      const firstSetting = Object.values(settingsMap)[0];
      if (firstSetting) {
        setOfflineGlobalSettings({
          selfie_required: firstSetting.require_selfie,
          max_offline_duration: firstSetting.max_offline_duration,
          gps_radius: firstSetting.gps_validation_radius
        });
      }
      
    } catch (error) {
      console.error('Error fetching sites and settings:', error);
      setErrorModal({
        show: true,
        message: 'Failed to load site settings. Please refresh the page.'
      });
    } finally {
      setLoadingSites(false);
    }
  };

  const handleMinPhotosChange = (value) => {
    setMinPhotosRequired(value);
    setHasChanges(true);
  };

  const handleAutoClockOutChange = (value) => {
    setAutoClockOut(value);
    setHasChanges(true);
  };

  const handleAutoClockOutTimeChange = (value) => {
    setAutoClockOutTime(value);
    setHasChanges(true);
  };

  const handleSelfieRequiredChange = (value) => {
    setSelfieRequired(value);
    setHasChanges(true);
  };

  const handleBankHolidaysEnabledChange = (value) => {
    setBankHolidaysEnabled(value);
    setHasChanges(true);
    
    // If enabling bank holidays, fetch them
    if (value && bankHolidays.length === 0) {
      fetchBankHolidays();
    }
  };

  const handleCompanyClosuresEnabledChange = (value) => {
    setCompanyClosuresEnabled(value);
    setHasChanges(true);
  };

  // Offline Clock In/Out handlers
  const handleSiteOfflineToggle = (siteId) => {
    const currentSettings = siteSettings[siteId] || {};
    const hasValidGPS = currentSettings.site_latitude && currentSettings.site_longitude;
    
    if (!hasValidGPS && !currentSettings.allow_offline_clockin) {
      // Show tooltip/error - user needs to update site address first
      setErrorModal({
        show: true,
        message: 'Please update the site address under Site Management before enabling offline clock-in. GPS coordinates are required.'
      });
      return;
    }
    
    setSiteSettings(prev => ({
      ...prev,
      [siteId]: {
        ...prev[siteId],
        allow_offline_clockin: !prev[siteId].allow_offline_clockin
      }
    }));
    setHasChanges(true);
  };

  const handleOfflineGlobalSettingChange = (field, value) => {
    setOfflineGlobalSettings(prev => ({
      ...prev,
      [field]: value
    }));
    setHasChanges(true);
  };

  // DEPRECATED: Old function kept for backward compatibility if needed
  const handleSiteOpeningHoursChange = (field, value) => {
    setSiteOpeningHours({
      ...siteOpeningHours,
      [field]: value
    });
    setHasChanges(true);
  };

  const handleAddCompanyClosure = async () => {
    if (!newClosurePeriod.name || !newClosurePeriod.start_date || !newClosurePeriod.end_date) {
      setErrorModal({
        show: true,
        message: 'Please fill in all required fields'
      });
      return;
    }

    if (new Date(newClosurePeriod.start_date) > new Date(newClosurePeriod.end_date)) {
      setErrorModal({
        show: true,
        message: 'Start date must be before end date'
      });
      return;
    }

    try {
      const response = await axios.post(`${API}/admin/company-closures`, newClosurePeriod);
      setCompanyClosures([...companyClosures, response.data]);
      setNewClosurePeriod({
        name: '',
        start_date: '',
        end_date: '',
        reason: ''
      });
      setHasChanges(true);
      setSuccessModal({
        show: true,
        message: 'Closure period added successfully!'
      });
    } catch (error) {
      setErrorModal({
        show: true,
        message: 'Failed to add closure period: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  const deleteCompanyClosure = (closureId) => {
    setConfirmModal({
      show: true,
      message: 'Are you sure you want to delete this closure period?',
      onConfirm: async () => {
        try {
          await axios.delete(`${API}/admin/company-closures/${closureId}`);
          setCompanyClosures(companyClosures.filter(closure => closure.id !== closureId));
          setHasChanges(true);
          setSuccessModal({
            show: true,
            message: 'Closure period deleted successfully!'
          });
        } catch (error) {
          setErrorModal({
            show: true,
            message: 'Failed to delete closure period: ' + (error.response?.data?.detail || 'Unknown error')
          });
        }
      }
    });
  };

  // Start editing a closure
  const startEditClosure = (closure) => {
    setEditingClosureId(closure.id);
    setEditClosureData({
      name: closure.name,
      start_date: closure.start_date,
      end_date: closure.end_date,
      reason: closure.reason || ''
    });
  };

  // Cancel editing
  const cancelEditClosure = () => {
    setEditingClosureId(null);
    setEditClosureData({
      name: '',
      start_date: '',
      end_date: '',
      reason: ''
    });
  };

  // Save edited closure
  const saveEditClosure = async () => {
    if (!editClosureData.name || !editClosureData.start_date || !editClosureData.end_date) {
      setErrorModal({
        show: true,
        message: 'Please fill in all required fields (name, start date, end date)'
      });
      return;
    }

    try {
      const response = await axios.put(
        `${API}/admin/company-closures/${editingClosureId}`,
        editClosureData,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      // Update the closure in the list
      setCompanyClosures(companyClosures.map(closure => 
        closure.id === editingClosureId ? response.data : closure
      ));
      
      setEditingClosureId(null);
      setEditClosureData({ name: '', start_date: '', end_date: '', reason: '' });
      setHasChanges(true);
      setSuccessModal({
        show: true,
        message: 'Closure period updated successfully!'
      });
    } catch (error) {
      setErrorModal({
        show: true,
        message: 'Failed to update closure period: ' + (error.response?.data?.detail || 'Unknown error')
      });
    }
  };

  // SAVE FUNCTION - Saves ONLY admin settings (company details are read-only)
  const saveSettings = async () => {
    try {
      setSaving(true);
      
      // Only admin settings (no company details)
      // Note: Company closures are managed separately via /admin/company-closures endpoints
      const settingsData = {
        // Configuration settings only
        min_photos_required: minPhotosRequired,
        auto_clock_out: autoClockOut,
        auto_clock_out_time: autoClockOutTime,
        selfie_required: selfieRequired,
        bank_holidays_enabled: bankHolidaysEnabled,
        company_closures_enabled: companyClosuresEnabled,
        use_same_hours_for_all: siteOpeningHours.use_same_hours_for_all,
        day_specific_hours: siteOpeningHours.day_specific_hours,
        // DEPRECATED fields (keep for backward compatibility)
        working_days: siteOpeningHours.working_days,
        start_time: siteOpeningHours.start_time,
        end_time: siteOpeningHours.end_time,
        daily_break_duration: siteOpeningHours.daily_break_duration
      };

      // Save admin settings only (company details excluded)
      await axios.post(`${API}/admin/organisation`, settingsData, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      // Save offline settings for each site
      const offlineSettingsPromises = Object.entries(siteSettings).map(([siteId, settings]) => {
        return axios.put(
          `${API}/admin/sites/${siteId}/offline-settings`,
          {
            allow_offline_clockin: settings.allow_offline_clockin,
            gps_validation_radius: offlineGlobalSettings.gps_radius,
            require_selfie: offlineGlobalSettings.selfie_required,
            max_offline_duration: offlineGlobalSettings.max_offline_duration,
            site_latitude: settings.site_latitude,
            site_longitude: settings.site_longitude
          },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
        );
      });
      
      await Promise.all(offlineSettingsPromises);
      
      // Re-fetch to get the updated data
      await fetchAllSettings();
      await fetchSitesAndSettings();
      setHasChanges(false);
      setOrganisationMessage('Settings saved successfully!');
      setSuccessModal({
        show: true,
        message: 'Settings saved successfully!'
      });
    } catch (error) {
      console.error('Failed to save settings:', error);
      setOrganisationMessage('Failed to save settings. Please try again.');
      
      // Handle different error formats (string, array of validation errors, or object)
      let errorMessage = 'Unknown error';
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation errors come as array of objects
        errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
      } else if (detail) {
        errorMessage = JSON.stringify(detail);
      }
      
      setErrorModal({
        show: true,
        message: 'Failed to save settings: ' + errorMessage
      });
    } finally {
      setSaving(false);
      setOrganisationLoading(false);
    }
  };

  const resetChanges = () => {
    if (settings) {
      setMinPhotosRequired(settings.min_photos_required);
      setAutoClockOut(settings.auto_clock_out || false);
      setAutoClockOutTime(settings.auto_clock_out_time || '18:00');
      setSelfieRequired(settings.selfie_required || false);
      setBankHolidaysEnabled(settings.bank_holidays_enabled || false);
      setCompanyClosuresEnabled(settings.company_closures_enabled || false);
      setSiteOpeningHours({
        use_same_hours_for_all: settings.use_same_hours_for_all ?? true,
        day_specific_hours: settings.day_specific_hours || {
          monday: { enabled: true, start_time: '08:00', end_time: '17:00' },
          tuesday: { enabled: true, start_time: '08:00', end_time: '17:00' },
          wednesday: { enabled: true, start_time: '08:00', end_time: '17:00' },
          thursday: { enabled: true, start_time: '08:00', end_time: '17:00' },
          friday: { enabled: true, start_time: '08:00', end_time: '17:00' },
          saturday: { enabled: false, start_time: '08:00', end_time: '17:00' },
          sunday: { enabled: false, start_time: '08:00', end_time: '17:00' }
        },
        // DEPRECATED fields
        working_days: settings.working_days || ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
        start_time: settings.start_time || '08:00',
        end_time: settings.end_time || '17:00',
        daily_break_duration: settings.daily_break_duration || 1
      });
      if (settings.company_closures) {
        setCompanyClosures(settings.company_closures);
      }
    }
    setHasChanges(false);
  };

  // Section toggle handler
  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 -m-8 p-8 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
        <span className="ml-3 text-gray-400">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 -m-8 p-8 space-y-6">
      {/* Modern Header - Dark Theme */}
      <div className="bg-gradient-to-r from-purple-600 via-purple-700 to-indigo-700 rounded-xl p-6 text-white shadow-lg">
        <h1 className="text-2xl sm:text-3xl font-bold mb-2">Admin Settings</h1>
        <p className="text-purple-200">Configure global settings that affect all team members' experience with the SiteOrbit system</p>
      </div>

      {/* Collapsible Settings Table - Dark Theme */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 overflow-hidden">
        
        {/* Organisation Details Section */}
        <CollapsibleSection 
          sectionId="organisationDetails"
          isOpen={openSections.organisationDetails}
          onToggle={toggleSection}
          title="Organisation Details"
          tagline="Company information (read-only)"
          icon={<path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-6a1 1 0 00-1-1H9a1 1 0 00-1 1v6a1 1 0 01-1 1H4a1 1 0 110-2V4z" clipRule="evenodd"></path>}
          bgColor="bg-purple-700"
          hoverColor="hover:bg-purple-600"
        >
          {/* Read-only company details - Contact customer support to update */}
          <div className="mb-4 bg-amber-500/20 border border-amber-500/30 rounded-lg p-3">
            <p className="text-sm text-amber-300 flex items-center">
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
              </svg>
              <strong>Note:</strong>&nbsp;If you need to amend your Organisation details, please contact customer support.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Debug Info */}
              {(!organisationData.name && !organisationData.email) && (
                <div className="md:col-span-3 bg-red-500/200/20 border border-red-500/30 rounded-lg p-3">
                  <p className="text-sm text-red-400">
                    ⚠️ No organisation data loaded. Check console logs.
                  </p>
                </div>
              )}
              
              {/* Company Name - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Company Name
                </label>
                <input
                  type="text"
                  value={organisationData.name || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm bg-slate-700 text-gray-400 cursor-not-allowed text-sm"
                  placeholder="No data"
                />
              </div>

              {/* Email - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  value={organisationData.email || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm bg-slate-700 text-gray-400 cursor-not-allowed text-sm"
                  placeholder="No data"
                />
              </div>

              {/* Phone - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={organisationData.phone || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm bg-slate-700 text-gray-400 cursor-not-allowed text-sm"
                  placeholder="No data"
                />
              </div>

              {/* Website - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={organisationData.website || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm bg-slate-700 text-gray-400 cursor-not-allowed text-sm"
                  placeholder="No data"
                />
              </div>

              {/* Company Number - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Company Number
                </label>
                <input
                  type="text"
                  value={organisationData.company_number || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm bg-slate-700 text-gray-400 cursor-not-allowed text-sm"
                  placeholder="No data"
                />
              </div>

              {/* Organisation ID - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Organisation ID
                </label>
                <input
                  type="text"
                  value={organisationData.organisation_id || 'Not assigned'}
                  readOnly
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm bg-slate-700 text-gray-400 cursor-not-allowed text-sm font-mono"
                  placeholder="No data"
                />
                <p className="mt-1 text-xs text-gray-400">
                  This ID is automatically generated for your organisation
                </p>
              </div>

              {/* Logo Upload */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Organisation Logo
                </label>
                <div className="flex items-start gap-4">
                  {/* Logo Preview */}
                  <div className="flex-shrink-0">
                    {(logoPreview || organisationData.logo) ? (
                      <div className="relative">
                        <img 
                          src={logoPreview || organisationData.logo} 
                          alt="Logo" 
                          className="w-20 h-20 object-contain border-2 border-slate-600 rounded-lg bg-slate-700"
                        />
                        {organisationData.logo && !logoPreview && (
                          <button
                            type="button"
                            onClick={handleLogoDelete}
                            disabled={uploadingLogo}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 transition-colors disabled:opacity-50"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        )}
                      </div>
                    ) : (
                      <div className="w-20 h-20 border-2 border-dashed border-slate-600 rounded-lg flex items-center justify-center bg-slate-700">
                        <svg className="w-8 h-8 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Upload Controls */}
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoFileChange}
                      className="hidden"
                      id="org-logo-upload"
                    />
                    <div className="flex gap-2">
                      <label
                        htmlFor="org-logo-upload"
                        className="inline-flex items-center px-3 py-2 border border-purple-500 text-purple-400 rounded-lg cursor-pointer hover:bg-purple-500/10 transition-colors text-sm"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                        </svg>
                        {logoFile ? 'Change' : 'Select'}
                      </label>
                      {logoFile && (
                        <button
                          type="button"
                          onClick={handleLogoUpload}
                          disabled={uploadingLogo}
                          className="inline-flex items-center px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors text-sm disabled:opacity-50"
                        >
                          {uploadingLogo ? 'Uploading...' : 'Upload'}
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">PNG, JPG up to 2MB</p>
                    {logoError && <p className="text-red-400 text-xs mt-1">{logoError}</p>}
                    {logoFile && <p className="text-purple-400 text-xs mt-1">Selected: {logoFile.name}</p>}
                  </div>
                </div>
              </div>

              {/* Street Address - Read Only */}
              <div className="md:col-span-3">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Street Address
                </label>
                <input
                  type="text"
                  value={organisationData.street_address || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm bg-slate-700 text-gray-400 cursor-not-allowed text-sm"
                />
              </div>

              {/* City - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  City
                </label>
                <input
                  type="text"
                  value={organisationData.city || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm bg-slate-700 text-gray-400 cursor-not-allowed text-sm"
                />
              </div>

              {/* Postcode - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Postcode
                </label>
                <input
                  type="text"
                  value={organisationData.postcode || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm bg-slate-700 text-gray-400 cursor-not-allowed text-sm"
                />
              </div>

              {/* Country - Read Only */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value={organisationData.country || ''}
                  readOnly
                  className="w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm bg-slate-700 text-gray-400 cursor-not-allowed text-sm"
                />
              </div>
            </div>
        </CollapsibleSection>

        {/* Offline Clock In / Out Section */}
        <CollapsibleSection 
          sectionId="offlineClockIn"
          isOpen={openSections.offlineClockIn}
          onToggle={toggleSection}
          title="Offline Clock In / Out"
          tagline="Mobile offline attendance configuration"
          icon={<path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" />}
          bgColor="bg-purple-700"
          hoverColor="hover:bg-purple-600"
        >
          <div className="space-y-6">
            {/* Subscription Feature Lock Notice */}
            {subscriptionFeatures && !subscriptionFeatures.offlineMode && (
              <div className="bg-amber-500/20 border-2 border-amber-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-amber-400 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="font-bold text-sm text-amber-300 mb-1">Feature Locked</p>
                    <p className="text-amber-400 text-sm leading-relaxed">
                      This feature is not available on your current subscription tier. Please visit the <span className="font-semibold">Plans Page</span> to upgrade your current subscription package.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Sites Table */}
            <div>
              <h3 className="text-md font-semibold text-gray-300 mb-3">Site Configuration</h3>
              {loadingSites ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : sites.length === 0 ? (
                <div className="text-center py-8 bg-slate-700/50 rounded-lg">
                  <p className="text-gray-400">No sites available. Please create sites first.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-slate-700">
                    <thead className="bg-slate-700/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Site Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Address</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">GPS Coordinates</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-300 uppercase">Offline Enabled</th>
                      </tr>
                    </thead>
                    <tbody className="bg-slate-800 divide-y divide-slate-700">
                      {sites.map(site => {
                        const settings = siteSettings[site.id] || {};
                        const hasValidGPS = settings.site_latitude && settings.site_longitude;
                        
                        return (
                          <tr key={site.id} className="hover:bg-slate-700/50">
                            <td className="px-4 py-3 text-sm text-white">{site.site_name}</td>
                            <td className="px-4 py-3 text-sm text-gray-400">
                              {site.address ? (
                                <span className="text-gray-300">{site.address}</span>
                              ) : (
                                <span className="text-gray-400 italic text-xs">No address</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-400">
                              {hasValidGPS ? (
                                <span className="text-green-400">
                                  {settings.site_latitude?.toFixed(4)}, {settings.site_longitude?.toFixed(4)}
                                </span>
                              ) : (
                                <span className="text-red-400 text-xs">No GPS - Update site address</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="flex items-center justify-center">
                                {!hasValidGPS && (
                                  <div className="group relative mr-2">
                                    <svg className="w-4 h-4 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
                                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                    <div className="hidden group-hover:block absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-48 p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-10">
                                      Update site address under Site Management to enable offline clock-in
                                    </div>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSiteOfflineToggle(site.id);
                                  }}
                                  disabled={!hasValidGPS && !settings.allow_offline_clockin || (subscriptionFeatures && !subscriptionFeatures.offlineMode)}
                                  className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                                    settings.allow_offline_clockin && subscriptionFeatures?.offlineMode ? 'bg-green-600' : 'bg-slate-600'
                                  } ${(!hasValidGPS && !settings.allow_offline_clockin) || (subscriptionFeatures && !subscriptionFeatures.offlineMode) ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                  <span
                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                      settings.allow_offline_clockin && subscriptionFeatures?.offlineMode ? 'translate-x-5' : 'translate-x-0'
                                    }`}
                                  />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Global Settings */}
            <div className="border-t pt-6 space-y-4">
              <h3 className="text-md font-semibold text-gray-300 mb-3">Global Offline Settings</h3>
              
              {/* Hierarchical Notice - Show when global selfie is ON */}
              {selfieRequired && (
                <div className="bg-amber-500/20 border border-amber-500/30 rounded-lg p-3 flex items-start">
                  <svg className="w-5 h-5 text-amber-400 mr-2 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-xs">
                    <p className="font-semibold text-amber-300">Global Selfie Override Active</p>
                    <p className="text-amber-400 mt-1">
                      The global "Selfie Verification" setting is currently ON. This means selfies are ALWAYS required for all clock-in/out operations (online and offline), regardless of the offline-specific setting below.
                    </p>
                  </div>
                </div>
              )}
              
              {/* Three settings in a row */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Selfie Requirement Toggle */}
                <div className={`flex flex-col p-4 rounded-lg border ${
                  selfieRequired 
                    ? 'bg-slate-700/30 border-slate-600 opacity-60' 
                    : 'bg-slate-700/50 border-slate-700'
                }`}>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-300">
                      Require Selfie for Offline
                    </label>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (!selfieRequired && subscriptionFeatures?.offlineMode) {
                          handleOfflineGlobalSettingChange('selfie_required', !offlineGlobalSettings.selfie_required);
                        }
                      }}
                      disabled={selfieRequired || (subscriptionFeatures && !subscriptionFeatures.offlineMode)}
                      className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                        selfieRequired || (subscriptionFeatures && !subscriptionFeatures.offlineMode)
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : offlineGlobalSettings.selfie_required 
                            ? 'bg-purple-600 cursor-pointer' 
                            : 'bg-slate-600 cursor-pointer'
                      }`}
                    >
                      <span
                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          (selfieRequired || offlineGlobalSettings.selfie_required) ? 'translate-x-5' : 'translate-x-0'
                        }`}
                      />
                    </button>
                  </div>
                  {selfieRequired && (
                    <span className="mb-1 px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs font-semibold rounded w-fit">
                      OVERRIDDEN
                    </span>
                  )}
                  <p className="text-xs text-gray-400 mt-auto">
                    {selfieRequired 
                      ? 'Global selfie verification overrides this'
                      : 'Selfie required when clocking offline'
                    }
                  </p>
                </div>
              
                {/* Max Offline Duration */}
                <div className="flex flex-col p-4 rounded-lg border bg-slate-700/50 border-slate-700">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Max Offline Duration
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="168"
                    value={offlineGlobalSettings.max_offline_duration}
                    onChange={(e) => handleOfflineGlobalSettingChange('max_offline_duration', parseInt(e.target.value))}
                    disabled={subscriptionFeatures && !subscriptionFeatures.offlineMode}
                    className={`block w-full px-3 py-2 border border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-slate-700 text-white ${
                      subscriptionFeatures && !subscriptionFeatures.offlineMode ? 'bg-slate-600 cursor-not-allowed opacity-60' : ''
                    }`}
                  />
                  <p className="text-xs text-gray-400 mt-2">Hours before sync (1-168)</p>
                </div>
              
                {/* GPS Radius Slider */}
                <div className="flex flex-col p-4 rounded-lg border bg-slate-700/50 border-slate-700">
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    GPS Radius: {offlineGlobalSettings.gps_radius}m
                  </label>
                  <input
                    type="range"
                    min="200"
                    max="500"
                    step="50"
                    value={offlineGlobalSettings.gps_radius}
                    onChange={(e) => handleOfflineGlobalSettingChange('gps_radius', parseInt(e.target.value))}
                    disabled={subscriptionFeatures && !subscriptionFeatures.offlineMode}
                    className={`block w-full h-2 bg-slate-600 rounded-lg appearance-none ${
                      subscriptionFeatures && !subscriptionFeatures.offlineMode ? 'cursor-not-allowed opacity-60' : 'cursor-pointer accent-purple-500'
                    }`}
                  />
                  <div className="flex justify-between text-xs text-gray-400 mt-1">
                    <span>200m</span>
                    <span>350m</span>
                    <span>500m</span>
                  </div>
                  <p className="text-xs text-gray-400 mt-2">Distance for clock in/out</p>
                </div>
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Site Opening Hours Section */}
        <CollapsibleSection 
          sectionId="siteOpeningHours"
          isOpen={openSections.siteOpeningHours}
          onToggle={toggleSection}
          title="Site Opening Hours"
          tagline="Day-specific working schedule"
          icon={<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />}
          bgColor="bg-purple-700"
          hoverColor="hover:bg-purple-600"
        >
          <div className="space-y-6">
            {/* Same Hours for All Checkbox */}
            <div className="bg-slate-700/50 p-4 rounded-lg border border-slate-600">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={siteOpeningHours.use_same_hours_for_all}
                  onChange={(e) => {
                    const useSame = e.target.checked;
                    if (useSame) {
                      // Apply first enabled day's hours to all enabled days
                      const firstEnabledDay = Object.entries(siteOpeningHours.day_specific_hours).find(([_, data]) => data.enabled);
                      if (firstEnabledDay) {
                        const [_, firstData] = firstEnabledDay;
                        const updatedHours = {};
                        Object.keys(siteOpeningHours.day_specific_hours).forEach(day => {
                          updatedHours[day] = {
                            enabled: siteOpeningHours.day_specific_hours[day].enabled,
                            start_time: firstData.start_time,
                            end_time: firstData.end_time
                          };
                        });
                        setSiteOpeningHours({...siteOpeningHours, use_same_hours_for_all: true, day_specific_hours: updatedHours});
                        setHasChanges(true);  // Track changes
                      }
                    } else {
                      setSiteOpeningHours({...siteOpeningHours, use_same_hours_for_all: false});
                      setHasChanges(true);  // Track changes
                    }
                  }}
                  className="mr-3 h-4 w-4 text-purple-500 focus:ring-purple-500 border-slate-500 rounded bg-slate-600"
                />
                <div>
                  <span className="text-sm font-medium text-white">Use same hours for all working days</span>
                  <p className="text-xs text-gray-400">When checked, all enabled days will have the same start and end times</p>
                </div>
              </label>
            </div>

            {/* Day-Specific Hours Table */}
            <div className="overflow-hidden border border-slate-700 rounded-lg">
              <table className="min-w-full divide-y divide-slate-700">
                <thead className="bg-slate-700/50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Day</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Enabled</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">Start Time</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase">End Time</th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase"
                      title="Total working hours between start and end time (does not deduct breaks)."
                    >
                      <span className="inline-flex items-center gap-1" data-testid="hours-column-header">
                        Hours
                        <span className="text-gray-500 cursor-help" aria-hidden="true">ⓘ</span>
                      </span>
                    </th>
                    <th
                      className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase"
                      title="Working hours minus the daily break duration. This is the time team members are actually paid for."
                    >
                      <span className="inline-flex items-center gap-1" data-testid="paid-hours-column-header">
                        Paid Hours
                        <span className="text-gray-500 cursor-help" aria-hidden="true">ⓘ</span>
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-slate-800 divide-y divide-slate-700">
                  {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map(day => {
                    const dayData = siteOpeningHours.day_specific_hours[day] || { enabled: false, start_time: '08:00', end_time: '17:00' };
                    const startH = parseInt(dayData.start_time.split(':')[0]);
                    const startM = parseInt(dayData.start_time.split(':')[1]);
                    const endH = parseInt(dayData.end_time.split(':')[0]);
                    const endM = parseInt(dayData.end_time.split(':')[1]);
                    const totalHours = (endH - startH) + (endM - startM) / 60;
                    // Paid Hours = working hours minus the org-wide daily break.
                    // Floored at 0 so a short day with a long break doesn't go negative.
                    const breakHours = siteOpeningHours.daily_break_duration || 0;
                    const paidHours = Math.max(0, totalHours - breakHours);
                    // Render decimal hours as HH:MM (e.g. 8.5 -> "08:30").
                    // Math.round on minutes prevents float drift (e.g. 7.5h -> 449.999... -> 450).
                    const formatHM = (decimalHours) => {
                      const totalMins = Math.max(0, Math.round(decimalHours * 60));
                      const hh = Math.floor(totalMins / 60);
                      const mm = totalMins % 60;
                      return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
                    };

                    return (
                      <tr key={day} className={dayData.enabled ? 'bg-slate-800' : 'bg-slate-800/50'}>
                        <td className="px-4 py-3 text-sm font-medium text-white capitalize">{day}</td>
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={dayData.enabled}
                            onChange={(e) => {
                              setSiteOpeningHours({
                                ...siteOpeningHours,
                                day_specific_hours: {
                                  ...siteOpeningHours.day_specific_hours,
                                  [day]: {...dayData, enabled: e.target.checked}
                                }
                              });
                              setHasChanges(true);  // Track changes
                            }}
                            className="h-4 w-4 text-purple-500 focus:ring-purple-500 border-slate-500 rounded bg-slate-600"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="time"
                            value={dayData.start_time}
                            disabled={!dayData.enabled}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              if (siteOpeningHours.use_same_hours_for_all) {
                                // Apply to all enabled days
                                const updatedHours = {};
                                Object.keys(siteOpeningHours.day_specific_hours).forEach(d => {
                                  updatedHours[d] = {
                                    ...siteOpeningHours.day_specific_hours[d],
                                    start_time: siteOpeningHours.day_specific_hours[d].enabled ? newValue : siteOpeningHours.day_specific_hours[d].start_time
                                  };
                                });
                                setSiteOpeningHours({...siteOpeningHours, day_specific_hours: updatedHours});
                              } else {
                                // Apply to this day only
                                setSiteOpeningHours({
                                  ...siteOpeningHours,
                                  day_specific_hours: {
                                    ...siteOpeningHours.day_specific_hours,
                                    [day]: {...dayData, start_time: newValue}
                                  }
                                });
                              }
                              setHasChanges(true);  // Track changes
                            }}
                            className="block w-full px-2 py-1 border border-slate-600 rounded text-sm bg-slate-700 text-white disabled:bg-slate-600 disabled:text-gray-500"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="time"
                            value={dayData.end_time}
                            disabled={!dayData.enabled}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              if (siteOpeningHours.use_same_hours_for_all) {
                                // Apply to all enabled days
                                const updatedHours = {};
                                Object.keys(siteOpeningHours.day_specific_hours).forEach(d => {
                                  updatedHours[d] = {
                                    ...siteOpeningHours.day_specific_hours[d],
                                    end_time: siteOpeningHours.day_specific_hours[d].enabled ? newValue : siteOpeningHours.day_specific_hours[d].end_time
                                  };
                                });
                                setSiteOpeningHours({...siteOpeningHours, day_specific_hours: updatedHours});
                              } else {
                                // Apply to this day only
                                setSiteOpeningHours({
                                  ...siteOpeningHours,
                                  day_specific_hours: {
                                    ...siteOpeningHours.day_specific_hours,
                                    [day]: {...dayData, end_time: newValue}
                                  }
                                });
                              }
                              setHasChanges(true);  // Track changes
                            }}
                            className="block w-full px-2 py-1 border border-slate-600 rounded text-sm bg-slate-700 text-white disabled:bg-slate-600 disabled:text-gray-500"
                          />
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400" data-testid={`hours-cell-${day}`}>
                          {dayData.enabled ? formatHM(totalHours) : '-'}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-400" data-testid={`paid-hours-cell-${day}`}>
                          {dayData.enabled ? formatHM(paidHours) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Daily Break Duration — flexible HH:MM selector (00:00 to 24:00). */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Daily Break Duration
              </label>
              {(() => {
                const totalMinutes = Math.round((siteOpeningHours.daily_break_duration || 0) * 60);
                const hh = Math.min(24, Math.floor(totalMinutes / 60));
                const mm = Math.min(59, totalMinutes % 60);
                const setDuration = (newHh, newMm) => {
                  // Clamp & convert to decimal hours.
                  let h = Math.max(0, Math.min(24, Number(newHh) || 0));
                  let m = Math.max(0, Math.min(59, Number(newMm) || 0));
                  if (h === 24) m = 0; // 24:00 is the absolute ceiling.
                  setSiteOpeningHours({
                    ...siteOpeningHours,
                    // 6 decimals = sub-second precision; round-trips cleanly with
                    // Math.round(d * 60) downstream so 35 minutes stays exactly 35.
                    daily_break_duration: parseFloat((h + m / 60).toFixed(6)),
                  });
                  setHasChanges(true);
                };
                const quickPicks = [
                  { label: 'None', h: 0, m: 0 },
                  { label: '30m', h: 0, m: 30 },
                  { label: '45m', h: 0, m: 45 },
                  { label: '1h', h: 1, m: 0 },
                  { label: '1h 30m', h: 1, m: 30 },
                  { label: '2h', h: 2, m: 0 },
                ];
                return (
                  <>
                    <div className="flex items-center gap-2" data-testid="daily-break-duration-picker">
                      <div className="flex flex-col items-center">
                        <input
                          type="number"
                          min={0}
                          max={24}
                          step={1}
                          value={hh}
                          onChange={(e) => setDuration(e.target.value, hh === 24 ? 0 : mm)}
                          className="w-20 px-2 py-2 text-center border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-slate-700 text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          aria-label="Hours"
                          data-testid="daily-break-hours-input"
                        />
                        <span className="text-[10px] uppercase tracking-wide text-gray-500 mt-1">Hours</span>
                      </div>
                      <span className="text-2xl font-semibold text-gray-400 -mt-4">:</span>
                      <div className="flex flex-col items-center">
                        <input
                          type="number"
                          min={0}
                          max={hh === 24 ? 0 : 59}
                          step={1}
                          value={mm}
                          onChange={(e) => setDuration(hh, e.target.value)}
                          disabled={hh === 24}
                          className="w-20 px-2 py-2 text-center border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-slate-700 text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none disabled:opacity-50"
                          aria-label="Minutes"
                          data-testid="daily-break-minutes-input"
                        />
                        <span className="text-[10px] uppercase tracking-wide text-gray-500 mt-1">Minutes</span>
                      </div>
                    </div>
                    {/* Quick picks for common values */}
                    <div className="flex flex-wrap items-center gap-1.5 mt-3" data-testid="daily-break-quick-picks">
                      <span className="text-[11px] uppercase tracking-wide text-gray-500 mr-1">Quick:</span>
                      {quickPicks.map((qp) => {
                        const isActive = qp.h === hh && qp.m === mm;
                        return (
                          <button
                            key={qp.label}
                            type="button"
                            onClick={() => setDuration(qp.h, qp.m)}
                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors duration-150 border ${
                              isActive
                                ? 'bg-purple-600 border-purple-500 text-white shadow-sm'
                                : 'bg-slate-700 border-slate-600 text-gray-300 hover:bg-slate-600 hover:text-white'
                            }`}
                            data-testid={`daily-break-quick-pick-${qp.label.replace(/\s+/g, '-')}`}
                          >
                            {qp.label}
                          </button>
                        );
                      })}
                    </div>
                  </>
                );
              })()}
              <p className="mt-2 text-xs text-gray-400 bg-slate-700/50 p-3 rounded-lg border border-slate-700">
                <span className="font-medium">Note:</span> Total break time allocated per team member during their work day. Choose any value from 00:00 to 24:00. This affects capacity calculations and workload distribution.
              </p>
            </div>

            {/* Current Setting Indicator */}
            <div className="bg-purple-500/20 p-3 rounded-lg border border-purple-500/30">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-purple-400 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <div className="text-sm">
                  <div className="font-medium text-white">
                    {(() => {
                      // Count enabled days from day_specific_hours
                      const enabledDays = Object.entries(siteOpeningHours.day_specific_hours)
                        .filter(([_, data]) => data.enabled);
                      const enabledCount = enabledDays.length;
                      
                      // Get time range from enabled days
                      if (enabledCount === 0) {
                        return 'No working days configured';
                      }
                      
                      // Check if all enabled days have same hours
                      const firstDay = enabledDays[0][1];
                      const allSameHours = enabledDays.every(([_, data]) => 
                        data.start_time === firstDay.start_time && data.end_time === firstDay.end_time
                      );
                      
                      if (allSameHours || siteOpeningHours.use_same_hours_for_all) {
                        return `${enabledCount} days/week, ${firstDay.start_time} - ${firstDay.end_time}`;
                      } else {
                        return `${enabledCount} days/week (varied hours per day)`;
                      }
                    })()}
                  </div>
                  <div className="text-purple-300 mt-1">
                    Break Duration: {(() => {
                      const tm = Math.round((siteOpeningHours.daily_break_duration || 0) * 60);
                      if (tm === 0) return 'No breaks';
                      const h = Math.floor(tm / 60);
                      const m = tm % 60;
                      const hh = h > 0 ? `${h} hour${h !== 1 ? 's' : ''}` : '';
                      const mm = m > 0 ? `${m} minute${m !== 1 ? 's' : ''}` : '';
                      return [hh, mm].filter(Boolean).join(' ');
                    })()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* UK Bank Holidays Section */}
        <CollapsibleSection 
          sectionId="ukBankHolidays"
          isOpen={openSections.ukBankHolidays}
          onToggle={toggleSection}
          title="UK Bank Holidays"
          tagline="Enforce UK bank holiday restrictions"
          icon={<path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />}
          bgColor="bg-purple-700"
          hoverColor="hover:bg-purple-600"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700">
              <div>
                <label className="block text-sm font-medium text-gray-300">
                  Enable UK Bank Holidays
                </label>
                <p className="text-xs text-gray-400 mt-1">
                  Prevent check-ins on UK bank holidays
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleBankHolidaysEnabledChange(!bankHolidaysEnabled);
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  bankHolidaysEnabled ? 'bg-purple-600' : 'bg-slate-600'
                }`}
                disabled={saving}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    bankHolidaysEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {bankHolidaysEnabled && (
              <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                <h4 className="text-sm font-medium text-gray-300 mb-3 flex items-center">
                  🇬🇧 {new Date().getFullYear()} UK Bank Holidays
                  {loadingBankHolidays && (
                    <div className="ml-2 inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  )}
                </h4>
                
                {bankHolidays.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {bankHolidays.map((holiday, index) => (
                      <div key={index} className="bg-slate-700/50 p-3 rounded-lg border border-slate-600 shadow-sm">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-white text-sm">{holiday.title}</p>
                            <p className="text-xs text-gray-400">{new Date(holiday.date).toLocaleDateString('en-GB', { 
                              weekday: 'long', 
                              year: 'numeric', 
                              month: 'long', 
                              day: 'numeric' 
                            })}</p>
                          </div>
                          <div className="text-right">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-500/20 text-red-400">
                              {new Date(holiday.date) > new Date() ? 'Upcoming' : 'Past'}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-4">
                    {loadingBankHolidays ? (
                      <p className="text-gray-400 text-sm">Loading bank holidays...</p>
                    ) : (
                      <p className="text-gray-400 text-sm">No bank holidays data available</p>
                    )}
                  </div>
                )}

                <div className="mt-4 pt-3 border-t border-slate-700">
                  <button
                    onClick={fetchBankHolidays}
                    disabled={loadingBankHolidays || saving}
                    className="inline-flex items-center px-3 py-2 border border-slate-600 shadow-sm text-sm font-medium rounded-md text-gray-300 bg-slate-700 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50"
                  >
                    <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clipRule="evenodd" />
                    </svg>
                    {loadingBankHolidays ? 'Refreshing...' : 'Refresh Holidays'}
                  </button>
                </div>
              </div>
            )}

            <div className="bg-indigo-500/20 p-4 rounded-lg border border-indigo-500/30">
              <div className="flex items-center text-indigo-300">
                <svg className="w-5 h-5 mr-2 text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="font-medium text-sm">
                  Current Setting: {bankHolidaysEnabled ? 'UK Bank Holidays Enforced' : 'No Holiday Restrictions'}
                </span>
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Company Closures Section */}
        <CollapsibleSection 
          sectionId="companyClosures"
          isOpen={openSections.companyClosures}
          onToggle={toggleSection}
          title="Company Closures"
          tagline="Custom company closure dates"
          icon={<path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-.293.707L12 11.414V15a1 1 0 01-.293.707l-2 2A1 1 0 018 17v-5.586L3.293 6.707A1 1 0 013 6V4z" clipRule="evenodd" />}
          bgColor="bg-purple-700"
          hoverColor="hover:bg-purple-600"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700">
              <div>
                <label className="block text-sm font-medium text-gray-300">
                  Enable Company Closures
                </label>
                <p className="text-xs text-gray-400 mt-1">
                  Allow custom closure periods during the year
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCompanyClosuresEnabledChange(!companyClosuresEnabled);
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  companyClosuresEnabled ? 'bg-purple-600' : 'bg-slate-600'
                }`}
                disabled={saving}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    companyClosuresEnabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {companyClosuresEnabled && (
              <div className="p-4 bg-slate-800 rounded-lg border border-slate-700 space-y-4">
                <h4 className="text-sm font-medium text-gray-300 mb-3">Add Closure Period</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="text"
                    placeholder="Closure name (e.g., Christmas Break)"
                    value={newClosurePeriod.name}
                    onChange={(e) => setNewClosurePeriod({...newClosurePeriod, name: e.target.value})}
                    className="block w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                  />
                  <input
                    type="text"
                    placeholder="Reason (optional)"
                    value={newClosurePeriod.reason}
                    onChange={(e) => setNewClosurePeriod({...newClosurePeriod, reason: e.target.value})}
                    className="block w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={newClosurePeriod.start_date}
                      onChange={(e) => setNewClosurePeriod({...newClosurePeriod, start_date: e.target.value})}
                      className="block w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-400 mb-1">End Date</label>
                    <input
                      type="date"
                      value={newClosurePeriod.end_date}
                      onChange={(e) => setNewClosurePeriod({...newClosurePeriod, end_date: e.target.value})}
                      className="block w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                    />
                  </div>
                </div>
                
                <button
                  onClick={handleAddCompanyClosure}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Add Closure Period
                </button>

                {/* Display existing closures */}
                {companyClosures.length > 0 && (
                  <div className="mt-4 space-y-2">
                    <h5 className="text-xs font-medium text-gray-300 mb-2">Current Closure Periods</h5>
                    {companyClosures.map((closure, index) => (
                      <div key={index} className="bg-slate-700/50 p-3 rounded-lg border border-slate-600 shadow-sm">
                        {editingClosureId === closure.id ? (
                          /* Edit Mode */
                          <div className="space-y-3">
                            <div>
                              <label className="block text-xs text-gray-400 mb-1">Name *</label>
                              <input
                                type="text"
                                value={editClosureData.name}
                                onChange={(e) => setEditClosureData({...editClosureData, name: e.target.value})}
                                className="block w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs text-gray-400 mb-1">Reason</label>
                              <input
                                type="text"
                                value={editClosureData.reason}
                                onChange={(e) => setEditClosureData({...editClosureData, reason: e.target.value})}
                                className="block w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                              />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">Start Date *</label>
                                <input
                                  type="date"
                                  value={editClosureData.start_date}
                                  onChange={(e) => setEditClosureData({...editClosureData, start_date: e.target.value})}
                                  className="block w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-gray-400 mb-1">End Date *</label>
                                <input
                                  type="date"
                                  value={editClosureData.end_date}
                                  onChange={(e) => setEditClosureData({...editClosureData, end_date: e.target.value})}
                                  className="block w-full px-3 py-2 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm"
                                />
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 pt-2">
                              <button
                                onClick={saveEditClosure}
                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEditClosure}
                                className="inline-flex items-center px-3 py-1.5 border border-slate-600 text-xs font-medium rounded-md text-gray-300 bg-slate-700 hover:bg-slate-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          /* View Mode */
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-white text-sm">{closure.name}</p>
                              <p className="text-xs text-gray-400">
                                {new Date(closure.start_date).toLocaleDateString()} - {new Date(closure.end_date).toLocaleDateString()}
                              </p>
                              {closure.reason && (
                                <p className="text-xs text-gray-400 mt-1">{closure.reason}</p>
                              )}
                            </div>
                            <div className="flex items-center space-x-2">
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400">
                                {new Date(closure.start_date) > new Date() ? 'Upcoming' : 
                                 new Date(closure.end_date) >= new Date() ? 'Active' : 'Past'}
                              </span>
                              <button
                                onClick={() => startEditClosure(closure)}
                                className="text-purple-400 hover:text-purple-300 text-sm"
                                title="Edit closure"
                              >
                                ✏️
                              </button>
                              <button
                                onClick={() => deleteCompanyClosure(closure.id)}
                                className="text-red-400 hover:text-red-400 text-sm"
                                title="Delete closure"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="bg-indigo-500/20 p-3 rounded-lg border border-indigo-500/30">
              <div className="flex items-center text-indigo-300">
                <svg className="w-5 h-5 mr-2 text-indigo-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="font-medium text-sm">
                  Current Setting: {companyClosuresEnabled ? 'Company Closures Enforced' : 'No Company Closure Restrictions'}
                </span>
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Photo Upload Requirements Section */}
        <CollapsibleSection 
          sectionId="photoRequirements"
          isOpen={openSections.photoRequirements}
          onToggle={toggleSection}
          title="Photo Upload Requirements"
          tagline="Control photo submission requirements"
          icon={<path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />}
          bgColor="bg-purple-700"
          hoverColor="hover:bg-purple-600"
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-3">
                Minimum Photos Required
              </label>
              
              <select
                value={minPhotosRequired}
                onChange={(e) => handleMinPhotosChange(parseInt(e.target.value))}
                className="block w-full px-4 py-3 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-slate-700 text-white"
                disabled={saving}
              >
                {Array.from({ length: 21 }, (_, i) => (
                  <option key={i} value={i}>
                    {i === 0 ? '0 (No requirement)' : `${i} photo${i !== 1 ? 's' : ''}`}
                  </option>
                ))}
              </select>
              
              <p className="mt-3 text-xs text-gray-400 bg-slate-700/50 p-3 rounded-lg border border-slate-700">
                <span className="font-medium">Note:</span> Select the minimum number of photos (0-20) that team members must upload before they can sign out from their shifts.
              </p>
            </div>

            <div className="bg-sky-500/20 p-4 rounded-lg border border-sky-500/30">
              <div className="flex items-center text-sky-300">
                <svg className="w-5 h-5 mr-2 text-sky-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="font-medium text-sm">Current Setting: {minPhotosRequired === 0 ? 'No photos required' : `${minPhotosRequired} photo${minPhotosRequired !== 1 ? 's' : ''} required`}</span>
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Auto Clock-Out Settings Section */}
        <CollapsibleSection 
          sectionId="autoClockOut"
          isOpen={openSections.autoClockOut}
          onToggle={toggleSection}
          title="Auto Clock-Out Settings"
          tagline="Automatic end-of-day management"
          icon={<path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />}
          bgColor="bg-purple-700"
          hoverColor="hover:bg-purple-600"
        >
          <div className="space-y-6">
            {/* Subscription Feature Lock Notice */}
            {subscriptionFeatures && !subscriptionFeatures.autoClockOut && (
              <div className="bg-amber-500/20 border-2 border-amber-500/30 rounded-lg p-4 mb-4">
                <div className="flex items-start">
                  <svg className="w-6 h-6 text-amber-400 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  <div>
                    <p className="font-bold text-sm text-amber-300 mb-1">Feature Locked</p>
                    <p className="text-amber-400 text-sm leading-relaxed">
                      This feature is not available on your current subscription tier. Please visit the <span className="font-semibold">Plans Page</span> to upgrade your current subscription package.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Auto Clock-Out Information */}
            <div className="bg-slate-700/50 border border-slate-600 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-6 h-6 text-sky-400 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path>
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-white mb-2">Auto Clock-Out Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-300">
                    <div className="space-y-1">
                      <div className="flex items-start">
                        <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></div>
                        <span>Automatically clocks out users who haven't manually clocked out by the specified time</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></div>
                        <span>Only applies to users who are currently clocked in and haven't completed their shift</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></div>
                        <span><strong>Photo requirements are bypassed</strong> - users are clocked out regardless of photo upload status</span>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-start">
                        <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></div>
                        <span>The system will mark these as "forced logout" with admin override reason</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></div>
                        <span>Users will see a notification when they next access the system about the auto clock-out</span>
                      </div>
                      <div className="flex items-start">
                        <div className="w-1.5 h-1.5 bg-purple-500 rounded-full mr-2 mt-1.5 flex-shrink-0"></div>
                        <span>Encourage users to upload photos and clock out manually before the auto clock-out time</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Auto Clock-Out Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700">
              <div>
                <label className="block text-sm font-medium text-gray-300">
                  Enable Auto Clock-Out
                </label>
                <p className="text-xs text-gray-400 mt-1">
                  Automatically clock out users at a specified time
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (subscriptionFeatures?.autoClockOut) {
                    handleAutoClockOutChange(!autoClockOut);
                  }
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  autoClockOut && subscriptionFeatures?.autoClockOut ? 'bg-purple-600' : 'bg-slate-600'
                } ${
                  (subscriptionFeatures && !subscriptionFeatures.autoClockOut) || saving ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                }`}
                disabled={saving || (subscriptionFeatures && !subscriptionFeatures.autoClockOut)}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    autoClockOut && subscriptionFeatures?.autoClockOut ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Auto Clock-Out Time (shown only when enabled) */}
            {autoClockOut && subscriptionFeatures?.autoClockOut && (
              <div className="p-4 bg-slate-800 rounded-lg border border-slate-700">
                <label className="block text-sm font-medium text-gray-300 mb-3">
                  Auto Clock-Out Time
                </label>
                <input
                  type="time"
                  value={autoClockOutTime}
                  onChange={(e) => handleAutoClockOutTimeChange(e.target.value)}
                  className="block w-full px-4 py-3 border border-slate-600 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 text-sm bg-slate-700 text-white"
                  disabled={saving}
                />
                <p className="mt-2 text-xs text-gray-400">
                  Users will be automatically clocked out at this time if they haven't already done so
                </p>
              </div>
            )}

            <div className="bg-sky-500/20 p-4 rounded-lg border border-sky-500/30">
              <div className="flex items-center text-sky-300">
                <svg className="w-5 h-5 mr-2 text-sky-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="font-medium text-sm">
                  Current Setting: {autoClockOut ? `Auto Clock-Out at ${autoClockOutTime}` : 'Auto Clock-Out Disabled'}
                </span>
              </div>
            </div>
          </div>
        </CollapsibleSection>

        {/* Selfie Verification Section */}
        <CollapsibleSection 
          sectionId="selfieVerification"
          isOpen={openSections.selfieVerification}
          onToggle={toggleSection}
          title="Selfie Verification"
          tagline="Mandatory selfie for clock-in/out"
          icon={<path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />}
          bgColor="bg-purple-700"
          hoverColor="hover:bg-purple-600"
        >
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-slate-800 rounded-lg border border-slate-700">
              <div>
                <label className="block text-sm font-medium text-gray-300">
                  Require Selfie Verification
                </label>
                <p className="text-xs text-gray-400 mt-1">
                  Users must take a selfie when clocking in/out (applies to ALL clock-in/out operations)
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelfieRequiredChange(!selfieRequired);
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  selfieRequired ? 'bg-purple-600' : 'bg-slate-600'
                }`}
                disabled={saving}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    selfieRequired ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            <div className="bg-teal-500/20 p-4 rounded-lg border border-teal-500/30">
              <div className="flex items-center text-teal-300">
                <svg className="w-5 h-5 mr-2 text-teal-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <span className="font-medium text-sm">
                  Current Setting: {selfieRequired ? 'Selfie Verification Required' : 'Selfie Verification Not Required'}
                </span>
              </div>
            </div>
          </div>
        </CollapsibleSection>

      </div>
      
      {/* Spacer for fixed footer */}
      <div className="h-24"></div>
      
      {/* Fixed Action Panel at Bottom */}
      <div className="fixed bottom-0 left-64 right-0 bg-slate-800 border-t-2 border-slate-600 shadow-2xl z-50 px-8 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center">
            {hasChanges && (
              <div className="flex items-center text-amber-600 mr-4">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span className="font-medium">You have unsaved changes</span>
              </div>
            )}
          </div>
          
          <div className="flex space-x-3">
            {hasChanges && (
              <button
                onClick={resetChanges}
                className="px-6 py-2 text-gray-400 border border-slate-600 rounded-lg hover:bg-slate-700/50 text-sm font-medium transition-colors duration-200"
                disabled={saving}
              >
                Reset
              </button>
            )}
            
            <button
              onClick={saveSettings}
              disabled={!hasChanges || saving}
              className={`px-6 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                hasChanges
                  ? 'bg-yellow-600 text-white hover:bg-yellow-700 shadow-lg hover:shadow-2xl'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </div>
              ) : (
                'Save All Settings'
              )}
            </button>
          </div>
        </div>
      </div>
      
      {/* Custom Modals */}
      <ConfirmModal
        isOpen={confirmModal.show}
        message={confirmModal.message}
        onConfirm={() => {
          if (confirmModal.onConfirm) {
            confirmModal.onConfirm();
          }
          setConfirmModal({ show: false, message: '', onConfirm: null });
        }}
        onClose={() => setConfirmModal({ show: false, message: '', onConfirm: null })}
      />

      <SuccessModal
        isOpen={successModal.show}
        message={successModal.message}
        onClose={() => setSuccessModal({ show: false, message: '' })}
      />

      <ErrorModal
        isOpen={errorModal.show}
        message={errorModal.message}
        onClose={() => setErrorModal({ show: false, message: '' })}
      />
    </div>
  );
};

export default TeamDashboardSettings;